import { CommonModule } from '@angular/common';
import { Component, EventEmitter, forwardRef, Input, OnInit, Output } from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { Alignment } from '@app/core/enums/questionnaire.enums';
import { Question, Questionnaire } from '@app/core/models/questionnaire.model';

@Component({
  selector: 'app-radio-button',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './radio-button.html',
  styleUrl: './radio-button.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RadioButton),
      multi: true,
    },
  ],
})
export class RadioButton implements ControlValueAccessor, OnInit {
  @Input() questionnaire: Questionnaire | null = null;
  @Input() question: Question | null = null;
  @Input() questionNumber: number = 0;
  @Input() isInvalid: boolean = false;
  @Input() errorMessage: string = '';
  @Input() formControl?: FormControl;
  @Output() answerChange = new EventEmitter<{
    questionId: string;
    options: { optionId: string; optionSelected: string }[];
  }>();

  alignment = Alignment;
  selectedValue: string = '';
  othersText: string = '';
  disabled: boolean = false;
  private isUpdating: boolean = false;
  private othersOptionId: string | null = null;

  // ControlValueAccessor
  onChange = (value: string) => {};
  onTouched = () => {};

  getAlignmentClass(question: any): string {
    return question.questionSpecificOptions?.alignment === this.alignment.HORIZONTAL
      ? 'horizontal-choices'
      : 'vertical-choices';
  }

  isOthersOption(option: any): boolean {
    return option?.text?.toLowerCase().trim() === 'others';
  }

  isOthersSelected(): boolean {
    if (!this.selectedValue) return false;
    // Use cached othersOptionId for faster and more reliable check
    if (this.othersOptionId) {
      return this.selectedValue === this.othersOptionId;
    }
    // Fallback to finding the option
    if (!this.question?.questionOptions) return false;
    const selectedOption = this.question.questionOptions.find(
      (opt) => opt.optionId === this.selectedValue
    );
    return this.isOthersOption(selectedOption);
  }

  private findOthersOptionId(): string | null {
    if (!this.question?.questionOptions) return null;
    const othersOption = this.question.questionOptions.find((opt) => this.isOthersOption(opt));
    return othersOption?.optionId || null;
  }

  ngOnInit() {
    // Cache the Others option ID for faster lookups
    this.othersOptionId = this.findOthersOptionId();

    if (this.formControl) {
      this.formControl.valueChanges.subscribe((value) => {
        if (!this.isUpdating) {
          // Handle array values from parent (for consistency with checkboxes)
          const optionId = Array.isArray(value) ? value[0] : value;
          const previousValue = this.selectedValue;
          this.selectedValue = optionId || '';

          // Only clear others text if switching away from Others option
          if (previousValue === this.othersOptionId && optionId !== this.othersOptionId) {
            this.othersText = '';
          }
        }
      });
    }
  }

  writeValue(value: string): void {
    if (!this.isUpdating) {
      const optionId = Array.isArray(value) ? value[0] : value;
      const previousValue = this.selectedValue;
      this.selectedValue = optionId || '';

      // Only clear others text if switching away from Others option
      if (previousValue === this.othersOptionId && optionId !== this.othersOptionId) {
        this.othersText = '';
      }
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  selectOption(optionId: string) {
    if (this.disabled) return;

    this.isUpdating = true;
    this.selectedValue = optionId;

    // Clear others text if a different option is selected
    if (!this.isOthersSelected()) {
      this.othersText = '';
    }

    this.onChange(optionId);
    this.onTouched();
    this.emitAnswer();

    if (this.formControl) {
      this.formControl.setValue(optionId, { emitEvent: false });
      this.formControl.markAsTouched();
    }

    setTimeout(() => {
      this.isUpdating = false;
    }, 0);
  }

  onOthersTextChange(text: string) {
    this.othersText = text;
    // Emit answer immediately - writeValue will check if value changed and skip if not
    // This allows continuous typing without interruption
  }

  onOthersInputBlur() {
    // Ensure answer is emitted when user leaves the field
    this.emitAnswer();
    this.onTouched();
  }

  private emitAnswer() {
    if (!this.question?.questionId) return;

    const optionId = this.selectedValue;
    const optionSelected = this.isOthersSelected() ? this.othersText : '';

    this.answerChange.emit({
      questionId: this.question.questionId,
      options: [{ optionId, optionSelected }],
    });

    console.log('emitAnswer', {
      questionId: this.question.questionId,
      options: [{ optionId, optionSelected }],
    });
  }
}
